import './ui/styles.css';
import { captureBlocker, captureRoom } from './audio/capture.ts';
import { wavBlob } from './audio/wav.ts';
import { analyzeMeasurement, prepareIrForExport, type Measurement } from './dsp/measure.ts';
import { generateSweep, sweepSpec, type Sweep, type SweepPresetName } from './dsp/sweep.ts';
import { ABSORPTION_PRESETS } from './dsp/acoustics.ts';
import { hilbertEnvelope } from './dsp/analytic.ts';
import { toDb } from './dsp/util.ts';
import {
  DEMO_ROOMS,
  synthesizeImpulseResponse,
  synthesizeRecording,
  type DemoRoomName,
} from './sim/syntheticRoom.ts';
import { drawBars, drawPlot, plotTheme } from './ui/plot.ts';
import { buildRoomModel } from './dsp/roomModel.ts';
import { createRoomView, type RoomViewHandle } from './ui/roomView.ts';
import { initScanMode } from './ui/scanController.ts';


/**
 * How far above its local reverberation an arrival must stand before it is
 * called a surface rather than a bump in the tail. Roughly one false alarm in
 * 20000 range cells for a Rayleigh-distributed diffuse field.
 */
const CONFIDENT_EXCESS_DB = 12;

function need<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element as T;
}

const ui = {
  measure: need<HTMLButtonElement>('measure'),
  demo: need<HTMLButtonElement>('demo'),
  preset: need<HTMLSelectElement>('preset'),
  absorption: need<HTMLSelectElement>('absorption'),
  temperature: need<HTMLInputElement>('temperature'),
  demoRoom: need<HTMLSelectElement>('demoRoom'),
  status: need<HTMLParagraphElement>('status'),
  warnings: need<HTMLUListElement>('warnings'),
  results: need<HTMLElement>('results'),
  rt60: need<HTMLElement>('rt60'),
  rt60Note: need<HTMLElement>('rt60Note'),
  volume: need<HTMLElement>('volume'),
  volumeNote: need<HTMLElement>('volumeNote'),
  nearest: need<HTMLElement>('nearest'),
  nearestNote: need<HTMLElement>('nearestNote'),
  dynamicRange: need<HTMLElement>('dynamicRange'),
  qualityNote: need<HTMLElement>('qualityNote'),
  irPlot: need<HTMLCanvasElement>('irPlot'),
  rangePlot: need<HTMLCanvasElement>('rangePlot'),
  decayPlot: need<HTMLCanvasElement>('decayPlot'),
  bandPlot: need<HTMLCanvasElement>('bandPlot'),
  echoTable: need<HTMLTableElement>('echoTable'),
  stage: need<HTMLElement>('stage'),
  roomCanvas: need<HTMLCanvasElement>('roomCanvas'),
  roomOverlay: need<HTMLElement>('roomOverlay'),
  roomCaption: need<HTMLElement>('roomCaption'),
  dataPlaceholder: need<HTMLElement>('dataPlaceholder'),
  scanResults: need<HTMLElement>('scanResults'),
  roomFigures: need<HTMLElement>('roomFigures'),
  roomLegend: need<HTMLElement>('roomLegend'),
  mapLegend: need<HTMLElement>('mapLegend'),
  cloudToggleRow: need<HTMLElement>('cloudToggleRow'),
  downloadIr: need<HTMLButtonElement>('downloadIr'),
  downloadRaw: need<HTMLButtonElement>('downloadRaw'),
  downloadJson: need<HTMLButtonElement>('downloadJson'),
};

interface Session {
  measurement: Measurement;
  recording: Float32Array | Float64Array;
  label: string;
}

let session: Session | null = null;
let busy = false;
let roomView: RoomViewHandle | null = null;
let gpuAvailable = true;

/**
 * Bring up the WebGPU view, or explain its absence in place.
 *
 * Failure here is not fatal and must not look like it: the measurement is
 * complete and correct without a picture of it, and every number remains
 * available in the drawers below. So the stage explains itself and the data
 * opens by default instead.
 */
void createRoomView(ui.roomCanvas, ui.roomOverlay)
  .then((view) => {
    roomView = view;
  })
  .catch((error: unknown) => {
    gpuAvailable = false;
    const reason = error instanceof Error ? error.message : String(error);
    ui.roomCanvas.remove();
    ui.stage.classList.remove('is-live');
    const notice = document.createElement('p');
    notice.className = 'stage-unsupported';
    notice.innerHTML =
      '<strong>No WebGPU</strong>The three-dimensional view needs WebGPU, which this browser ' +
      'did not provide. Everything else works: measure as normal and open the data below.';
    notice.append(document.createTextNode(` (${reason})`));
    ui.stage.append(notice);
  });

for (const preset of ABSORPTION_PRESETS) {
  const option = document.createElement('option');
  option.value = preset.id;
  option.textContent = `${preset.label} - ${preset.hint}`;
  if (preset.id === 'typical') option.selected = true;
  ui.absorption.append(option);
}

/**
 * Let the browser paint before a long synchronous block.
 *
 * Deconvolving a 3-second capture and running five octave-band decays is
 * around a second of straight-line arithmetic on a desktop and several on a
 * phone. Without a yield the status line saying so never reaches the screen -
 * the app just freezes with the old text still showing.
 *
 * Raced against a timer rather than trusting the frame callback alone:
 * requestAnimationFrame does not fire while a tab is hidden, so a measurement
 * started and then backgrounded would wait for a frame that never comes and
 * leave the app stuck mid-run.
 */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    requestAnimationFrame(finish);
    setTimeout(finish, 32);
  });
}

function setStatus(message: string, kind: 'idle' | 'busy' | 'error' = 'idle'): void {
  ui.status.textContent = message;
  ui.status.className = `status${kind === 'idle' ? '' : ` ${kind}`}`;
}

function setWarnings(warnings: string[]): void {
  ui.warnings.replaceChildren(
    ...warnings.map((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      return item;
    }),
  );
}

function analysisOptions(): Parameters<typeof analyzeMeasurement>[2] {
  const alpha = ABSORPTION_PRESETS.find((p) => p.id === ui.absorption.value)?.alpha ?? 0.12;
  const temperatureC = Number.parseFloat(ui.temperature.value);
  return {
    temperatureC: Number.isFinite(temperatureC) ? temperatureC : 20,
    room: { alpha },
  };
}

async function runMeasurement(): Promise<void> {
  if (busy) return;
  const blocker = captureBlocker();
  if (blocker) {
    setStatus(blocker, 'error');
    return;
  }

  busy = true;
  ui.measure.disabled = true;
  ui.demo.disabled = true;
  ui.measure.classList.add('is-running');
  ui.measure.textContent = 'Measuring...';
  setWarnings([]);

  try {
    const capture = await captureRoom({
      preset: ui.preset.value as SweepPresetName,
      onStatus: (message) => setStatus(message, 'busy'),
    });
    setStatus('Deconvolving the sweep...', 'busy');
    await nextFrame();
    const measurement = analyzeMeasurement(capture.recording, capture.sweep, analysisOptions());
    show({ measurement, recording: capture.recording, label: capture.inputLabel }, [
      ...capture.warnings,
      ...measurement.quality.warnings,
    ]);
    setStatus(`Measured through ${capture.inputLabel} at ${capture.sampleRate} Hz.`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setStatus(
      message.includes('Permission') || message.includes('NotAllowed')
        ? 'Microphone permission denied. Chirpr cannot measure without it.'
        : `Measurement failed: ${message}`,
      'error',
    );
  } finally {
    busy = false;
    ui.measure.disabled = false;
    ui.demo.disabled = false;
    ui.measure.classList.remove('is-running');
    ui.measure.textContent = 'Single measurement';
  }
}

/**
 * Run the identical analysis over a simulated room.
 *
 * Not a mock of the results - a real recording of a synthetic room, pushed
 * through the same deconvolution and the same estimators. It is how the maths
 * gets checked against known ground truth, and it means the app is explorable
 * on a desktop with no microphone.
 */
async function runDemo(): Promise<void> {
  if (busy) return;
  busy = true;
  ui.demo.disabled = true;
  setStatus('Simulating a room and measuring it...', 'busy');
  await nextFrame();

  try {
    runDemoSync();
  } finally {
    busy = false;
    ui.demo.disabled = false;
  }
}

function runDemoSync(): void {
  const name = ui.demoRoom.value as DemoRoomName;
  const spec = DEMO_ROOMS[name];
  const sweep: Sweep = generateSweep(sweepSpec(48000, ui.preset.value as SweepPresetName));

  const ir = synthesizeImpulseResponse({
    ...spec,
    reflectors: [...spec.reflectors],
    sampleRate: 48000,
    lengthSec: Math.max(1.5, spec.rt60Sec * 2.5),
  });
  const recording = synthesizeRecording(sweep.samples, ir, 48000, { snrDb: 42 });
  const measurement = analyzeMeasurement(recording, sweep, analysisOptions());

  show({ measurement, recording, label: `simulated ${name}` }, [
    `Walls at ${spec.reflectors.map((r) => `${r.distanceM.toFixed(2)} m`).join(', ')}.`,
    ...measurement.quality.warnings,
  ]);
  setStatus(`Simulated ${name} · true RT60 ${spec.rt60Sec.toFixed(2)} s.`);
}

function show(next: Session, warnings: string[]): void {
  session = next;
  // One measurement and one scan make different claims about the same room, so
  // only one of them is ever on screen. Taking the data section back here is
  // the counterpart of the scan taking it in showMap.
  ui.results.hidden = false;
  ui.scanResults.hidden = true;
  ui.dataPlaceholder.hidden = true;
  ui.roomFigures.hidden = true;
  ui.roomLegend.hidden = false;
  ui.mapLegend.hidden = true;
  ui.cloudToggleRow.hidden = true;
  setWarnings(warnings);
  render();
}

function render(): void {
  if (!session) return;
  const { measurement } = session;
  const { decay, room, range, quality } = measurement;

  const fit = decay.best;
  ui.rt60.textContent = Number.isFinite(fit.rt60Sec) ? fit.rt60Sec.toFixed(2) : '—';
  ui.rt60Note.textContent = `${fit.metric}, straight-line fit ${(fit.r2 * 100).toFixed(1)}%${
    fit.valid ? '' : ' — treat with suspicion'
  }`;

  ui.volume.textContent = Number.isFinite(room.volumeM3) ? room.volumeM3.toFixed(0) : '—';
  const surface = ABSORPTION_PRESETS.find((p) => p.id === ui.absorption.value)?.label ?? 'typical';
  // Volume is cubic in the absorption guess, so the assumption and the spread
  // both belong on screen next to the number they produced.
  ui.volumeNote.textContent = `${room.volumeLowM3.toFixed(0)}–${room.volumeHighM3.toFixed(
    0,
  )} m³ assuming ${surface.toLowerCase()} surfaces — about ${room.equivalentCubeSideM.toFixed(
    1,
  )} m across, the sturdier figure`;

  // Prefer the nearest arrival that is actually trustworthy. Falling back to
  // the strongest one keeps something on screen in a live room, but it gets
  // labelled as the guess it is rather than printed as a distance.
  const confident = range.echoes.filter((e) => e.excessDb >= CONFIDENT_EXCESS_DB);
  const strongest = [...range.echoes].sort((a, b) => b.excessDb - a.excessDb)[0];
  const nearest = confident[0] ?? strongest;
  ui.nearest.textContent = nearest ? nearest.distanceM.toFixed(2) : '—';
  ui.nearestNote.textContent = !nearest
    ? 'Nothing stood clear of the reverberation'
    : confident.length > 0
      ? `${nearest.levelDb.toFixed(0)} dB, standing +${nearest.excessDb.toFixed(0)} dB above the reverb`
      : `only +${nearest.excessDb.toFixed(0)} dB above the reverb — could be the tail`;

  ui.dynamicRange.textContent = Math.abs(quality.usableDecayDb).toFixed(0);
  // The label already says what the number is; the note carries the second
  // quality signal instead of repeating it.
  const distortion = Number.isFinite(quality.distortionDb)
    ? `speaker distortion ${quality.distortionDb.toFixed(0)} dB`
    : 'speaker distortion not measurable';
  ui.qualityNote.textContent = `${distortion}, noise floor ${quality.noiseFloorDb.toFixed(0)} dB`;

  const roomModel = buildRoomModel(measurement);
  ui.roomCaption.textContent = roomModel.caption;
  if (roomView) {
    roomView.update(roomModel);
    ui.stage.classList.add('is-live');
  }

  drawImpulse(measurement);
  drawRange(measurement);
  drawDecay(measurement);
  drawBandChart(measurement);
  drawEchoTable(measurement);
}

function drawImpulse(measurement: Measurement): void {
  const { ir, sampleRate, directFractionalIndex, decay } = measurement;
  const spanMs = Math.min(400, Math.max(80, (decay.best.rt60Sec || 0.5) * 700));
  const from = Math.max(0, Math.round(directFractionalIndex - 0.005 * sampleRate));
  const to = Math.min(ir.length, Math.round(directFractionalIndex + (spanMs / 1000) * sampleRate));

  const envelope = hilbertEnvelope(ir.slice(from, to));
  // Reference the plot to the direct arrival, not to whatever the tail did.
  let peak = 0;
  const directWindow = Math.min(envelope.length, Math.round(0.01 * sampleRate));
  for (let i = 0; i < directWindow; i++) peak = Math.max(peak, envelope[i]);
  const x = new Float64Array(envelope.length);
  const y = new Float64Array(envelope.length);
  for (let i = 0; i < envelope.length; i++) {
    x[i] = ((from + i - directFractionalIndex) / sampleRate) * 1000;
    y[i] = toDb(envelope[i], peak);
  }

  const theme = plotTheme();
  drawPlot(ui.irPlot, [{ x, y, color: theme.ink, width: 1, fill: theme.fill }], {
    xMin: -5,
    xMax: spanMs,
    yMin: -70,
    yMax: 3,
    xLabel: 'milliseconds after the direct sound',
    yLabel: 'dB',
    formatX: (v) => v.toFixed(0),
    formatY: (v) => v.toFixed(0),
  });
}

function drawRange(measurement: Measurement): void {
  const { range } = measurement;
  const theme = plotTheme();
  drawPlot(
    ui.rangePlot,
    [
      {
        x: range.distancesM,
        y: range.levelsDb,
        color: theme.ink,
        width: 1,
        fill: theme.fill,
      },
    ],
    {
      xMin: 0,
      xMax: range.maxRangeM,
      yMin: -65,
      yMax: 3,
      xLabel: 'metres to the reflecting surface',
      yLabel: 'dB below direct',
      formatX: (v) => v.toFixed(0),
      formatY: (v) => v.toFixed(0),
      shades: [
        {
          from: 0,
          to: range.blindRangeM,
          color: theme.grid,
          label: 'blind',
        },
      ],
      markers: range.echoes.slice(0, 5).map((echo) => ({
        x: echo.distanceM,
        y: echo.levelDb,
        label: `${echo.distanceM.toFixed(2)} m`,
        color: echo.excessDb >= CONFIDENT_EXCESS_DB ? theme.accent : theme.faint,
      })),
      empty: 'No profile',
    },
  );
}

function drawDecay(measurement: Measurement): void {
  const { decay, sampleRate } = measurement;
  const count = decay.curveDb.length;
  const x = new Float64Array(count);
  for (let i = 0; i < count; i++) x[i] = i / sampleRate;
  const endSec = count / sampleRate;

  const theme = plotTheme();
  const fit = decay.best;
  const guides = fit.valid
    ? [
        {
          // Extend the fitted slope out to a full 60 dB, which is what RT60
          // means: the decay is measured over 20 or 30 dB and extrapolated,
          // because no real room stays above its noise floor for 60.
          from: [fit.startSec, fit.upperDb] as [number, number],
          to: [fit.startSec + fit.rt60Sec, fit.upperDb - 60] as [number, number],
          color: theme.accent,
          dash: [5, 4],
        },
      ]
    : [];

  drawPlot(ui.decayPlot, [{ x, y: decay.curveDb, color: theme.ink, width: 1.6 }], {
    xMin: 0,
    xMax: Math.max(endSec, fit.valid ? fit.rt60Sec * 1.05 : 0.1),
    yMin: -65,
    yMax: 3,
    xLabel: 'seconds',
    yLabel: 'dB',
    formatX: (v) => v.toFixed(2),
    formatY: (v) => v.toFixed(0),
    guides,
  });
}

function drawBandChart(measurement: Measurement): void {
  const bars = measurement.bands.map((band) => ({
    label: band.band.label,
    value: band.rt60Sec,
    dimmed: !band.valid,
  }));
  drawBars(ui.bandPlot, bars, {
    color: plotTheme().ink,
    empty: 'Octave bands are skipped for this measurement',
  });
}

function drawEchoTable(measurement: Measurement): void {
  const { echoes } = measurement.range;
  ui.echoTable.replaceChildren();

  if (echoes.length === 0) {
    const row = ui.echoTable.insertRow();
    const cell = row.insertCell();
    cell.className = 'empty';
    cell.colSpan = 4;
    cell.textContent =
      'Nothing stood clear of the reverberation. In a live room the tail can be louder than the walls that made it - the curve above is still the measurement.';
    return;
  }

  const head = ui.echoTable.createTHead().insertRow();
  for (const label of ['Surface', 'Delay', 'Level', 'Above reverb']) {
    const cell = document.createElement('th');
    cell.textContent = label;
    head.append(cell);
  }

  const body = ui.echoTable.createTBody();
  for (const echo of echoes) {
    const row = body.insertRow();
    if (echo.excessDb < CONFIDENT_EXCESS_DB) row.className = 'weak';
    row.insertCell().textContent = `${echo.distanceM.toFixed(2)} m`;
    row.insertCell().textContent = `${echo.delayMs.toFixed(2)} ms`;
    row.insertCell().textContent = `${echo.levelDb.toFixed(1)} dB`;
    row.insertCell().textContent = `+${echo.excessDb.toFixed(1)} dB`;
  }
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  const now = new Date();
  const pad = (v: number): string => String(v).padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(
    now.getMinutes(),
  )}${pad(now.getSeconds())}`;
}

ui.downloadIr.addEventListener('click', () => {
  if (!session) return;
  const ir = prepareIrForExport(session.measurement);
  download(
    wavBlob(ir, { sampleRate: session.measurement.sampleRate, format: 'float32' }),
    `chirpr-ir-${stamp()}.wav`,
  );
});

ui.downloadRaw.addEventListener('click', () => {
  if (!session) return;
  download(
    wavBlob(session.recording, { sampleRate: session.measurement.sampleRate, format: 'float32' }),
    `chirpr-raw-${stamp()}.wav`,
  );
});

ui.downloadJson.addEventListener('click', () => {
  if (!session) return;
  const { measurement } = session;
  const summary = {
    capturedFrom: session.label,
    sampleRate: measurement.sampleRate,
    sweep: {
      startHz: measurement.sweep.spec.startHz,
      endHz: measurement.sweep.spec.endHz,
      durationSec: measurement.sweep.spec.durationSec,
      usableLowHz: Math.round(measurement.sweep.usableLowHz),
      usableHighHz: Math.round(measurement.sweep.usableHighHz),
      harmonicSeparationSec: measurement.sweep.harmonicSeparationSec,
    },
    reverberation: {
      metric: measurement.decay.best.metric,
      rt60Sec: measurement.decay.best.rt60Sec,
      r2: measurement.decay.best.r2,
      edtSec: measurement.decay.edt.rt60Sec,
      t20Sec: measurement.decay.t20.rt60Sec,
      t30Sec: measurement.decay.t30.rt60Sec,
      byOctave: measurement.bands.map((b) => ({
        centerHz: b.band.centerHz,
        rt60Sec: b.rt60Sec,
        valid: b.valid,
      })),
    },
    room: measurement.room,
    echoes: measurement.range.echoes,
    quality: measurement.quality,
  };
  download(
    new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' }),
    `chirpr-${stamp()}.json`,
  );
});

ui.measure.addEventListener('click', () => void runMeasurement());
ui.demo.addEventListener('click', () => void runDemo());
for (const control of [ui.absorption, ui.temperature]) {
  control.addEventListener('change', () => {
    if (!session || busy) return;
    // Absorption and temperature only affect the estimates, not the capture -
    // so re-derive rather than asking the user to measure the room again.
    session.measurement = analyzeMeasurement(
      session.recording,
      session.measurement.sweep,
      analysisOptions(),
    );
    render();
  });
}

// Redraw when a drawer opens. A canvas inside a closed <details> may have had
// no layout when the plots were first drawn, and browsers disagree about
// whether it does - so rather than depend on that, redraw on reveal.
for (const drawer of document.querySelectorAll<HTMLDetailsElement>('.drawer')) {
  drawer.addEventListener('toggle', () => {
    if (drawer.open && session) render();
  });
}

let resizeTimer: number | undefined;
window.addEventListener('resize', () => {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(render, 120);
});

const blocker = captureBlocker();
if (blocker) {
  setStatus(`${blocker} You can still explore a simulated room.`, 'error');
}


/**
 * Mapping mode shares the stage with the single measurement, and takes it over
 * the first time a scan finishes. Wired here rather than inside the controller
 * so the two views never both hold the canvas.
 */
initScanMode({
  setStatus,
  setWarnings,
  isBusy: () => busy,
  setBusy: (value) => {
    busy = value;
  },
  temperatureC: () => Number(ui.temperature.value) || 20,
  demoScene: () => ui.demoRoom.value,
  releaseStage: () => {
    roomView?.destroy();
    roomView = null;
  },
  hasGpu: () => gpuAvailable,
  claimDataSection: () => {
    ui.results.hidden = true;
    ui.dataPlaceholder.hidden = true;
  },
});
